/**
 * What you have been playing.
 *
 * One grid in the order you played them, for the same reason Favorites is one
 * grid: the order IS the answer here, and sorting it into shelves would throw
 * away the only thing this list knows that the catalogue does not.
 *
 * A game you no longer own still shows. It is a record of what you did, not a
 * menu - and quietly dropping a game somebody remembers playing would read as
 * the list being broken rather than as the entitlement having lapsed.
 */

import { Link } from 'react-router-dom';
import { useCatalog } from '../use-catalog.js';
import { useRecent } from '../use-recent.js';
import { useSession } from '../session-context.js';
import { Icon } from '../render/Icon.js';
import { GameCard } from './GameCard.js';
import { useFavourites } from '../use-favourites.js';

export function Recent() {
  const { catalog, error, retry } = useCatalog();
  const { session } = useSession();
  const recent = useRecent();
  const favourites = useFavourites();

  if (error) {
    return (
      <div className="page narrow felt-page library-page">
        <h1 className="page-title page-heading">Recently Played</h1>
        <p className="banner error">{error}</p>
        <button type="button" className="big-button" onClick={retry}>
          Try again
        </button>
      </div>
    );
  }

  if (!catalog || recent.loading) return <div className="splash">Dealing…</div>;

  const games = recent.ids
    .map((id) => catalog.games.find((game) => game.id === id))
    .filter((game): game is NonNullable<typeof game> => game !== undefined);

  return (
    <div className="page felt-page library-page">
      <h1 className="page-title page-heading">Recently Played</h1>

      {session === null ? (
        <p className="page-lede">Sign in and your games will be waiting here.</p>
      ) : games.length === 0 ? (
        <>
          <p className="page-lede">Nothing played yet.</p>
          <p className="field-help">
            Deal a game and it will show up here, newest first.
          </p>
          <Link className="cta cta-primary" to="/library">
            <Icon name="gamepad" className="cta-mark" />
            <span className="cta-label">Your games</span>
            <Icon name="chevron-right" />
          </Link>
        </>
      ) : (
        <>
          <p className="page-lede">Newest first. Pick up where you left off.</p>
          <div className="game-grid">
            {games.map((game) => (
              <GameCard
                key={game.id}
                game={game}
                starred={favourites.has(game.id)}
                onStar={() => favourites.toggle(game.id)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

